import { useLemonSqueezy } from '@/composables/useLemonSqueezy';

const state = {
  products: [],
  loading: false,
  error: null,
  lastFetched: null,
  // Cache for 5 minutes (300000 ms)
  cacheTime: 5 * 60 * 1000
};

const mutations = {
  SET_PRODUCTS(state, products) {
    state.products = products;
    state.lastFetched = Date.now();
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_PRODUCTS(state) {
    state.products = [];
    state.lastFetched = null;
    state.error = null;
  }
};

const actions = {
  async fetchProducts({ commit, state, getters }) {
    // Check if we have cached data and it's still valid
    if (getters.isCacheValid && state.products.length > 0) {
      console.log('[Products Store] Returning products from cache/store');
      return state.products;
    }

    console.log('[Products Store] Fetching products from Lemon Squeezy API');
    const { fetchProductsWithPlans, validateSetup } = useLemonSqueezy();
    
    if (!validateSetup()) {
      const error = 'Lemon Squeezy is not properly configured. Please check environment variables.';
      commit('SET_ERROR', error);
      throw new Error(error);
    }

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const products = await fetchProductsWithPlans();
      
      // Sort products by price
      const sortedProducts = products
        .map(product => ({
          ...product,
          attributes: {
            ...product.attributes,
            features: product.attributes.features && product.attributes.features.length
              ? product.attributes.features
              : parseFeatures(product.attributes.description)
          }
        }))
        .sort((a, b) => {
          const aPrice = a.variants?.[0]?.attributes?.price ?? 0;
          const bPrice = b.variants?.[0]?.attributes?.price ?? 0;
          return aPrice - bPrice;
        });

      commit('SET_PRODUCTS', sortedProducts);
      return sortedProducts;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async refreshProducts({ commit }) {
    commit('CLEAR_PRODUCTS');
    return this.dispatch('products/fetchProducts');
  },

  clearProducts({ commit }) {
    commit('CLEAR_PRODUCTS');
  }
};

const getters = {
  isCacheValid: (state) => {
    if (!state.lastFetched) return false;
    return (Date.now() - state.lastFetched) < state.cacheTime;
  },
  
  hasProducts: (state) => state.products.length > 0,
  
  getProductById: (state) => (id) => {
    return state.products.find(product => product.id === id);
  },

  sortedProducts: (state) => {
    return [...state.products].sort((a, b) => {
      const aPrice = a.variants?.[0]?.attributes?.price ?? 0;
      const bPrice = b.variants?.[0]?.attributes?.price ?? 0;
      return aPrice - bPrice;
    });
  }
};

// Helper function to parse features from description
function parseFeatures(description) {
  if (!description) return [];
  let text = description.replace(/<\/?p>/g, '\n').replace(/<br\s*\/?>/g, '\n');
  return text.split('\n')
    .map(line => line.replace(/^- /, '').trim())
    .filter(line => line.length > 0 && line !== description);
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
